/**
 * Contact Page
 * Simple form. Direct communication.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Container, Section, Spacer } from '../components/primitives';
import { Headline, Body, Label } from '../components/primitives/Typography';
import Button from '../components/common/Button';
import { Input, Textarea } from '../components/common/Input';

export default function Contact() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await api.submitContact(form);
      setSuccess(true);
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main>
      {/* Hero */}
      <Section size="lg" className="pt-20 md:pt-32 pb-16">
        <Container>
          <div className="max-w-3xl">
            <Label>Contact</Label>
            <Headline className="mt-4">
              Get in touch
            </Headline>
            <Body className="mt-6 text-lg text-text-secondary">
              Have questions before booking a consultation? Not sure if we can help with your
              specific situation? Send us a message and we'll get back to you within 24-48 hours.
            </Body>
          </div>
        </Container>
      </Section>

      {/* Contact Form + Info */}
      <Section background="subtle">
        <Container>
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Form */}
            <div className="lg:col-span-7">
              <div className="bg-white p-8 md:p-10 border border-border-subtle">
                {success ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h2 className="text-h3 font-medium text-text-primary">Message sent</h2>
                    <p className="mt-3 text-text-secondary">
                      Thank you for reaching out. We'll get back to you within 24-48 hours.
                    </p>
                    <button
                      onClick={() => setSuccess(false)}
                      className="mt-6 text-small text-accent hover:underline"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <Input
                        label="Name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        required
                      />
                      <Input
                        label="Email"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <Input
                        label="Phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        hint="Optional"
                      />
                      <Input
                        label="Subject"
                        value={form.subject}
                        onChange={(e) => setForm({ ...form, subject: e.target.value })}
                        required
                      />
                    </div>
                    <Textarea
                      label="Message"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      rows={6}
                      required
                    />

                    {error && (
                      <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-small">
                        {error}
                      </div>
                    )}

                    <Button type="submit" isLoading={submitting} size="lg">
                      Send message
                    </Button>
                  </form>
                )}
              </div>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-5">
              <div className="sticky top-8 space-y-8">
                <div>
                  <h3 className="text-h4 font-medium text-text-primary">Contact Information</h3>
                  <div className="mt-6 space-y-4">
                    <div>
                      <p className="text-small text-text-tertiary">Email</p>
                      <a
                        href="mailto:hello@anvayee.com"
                        className="text-base text-text-primary hover:text-accent transition-colors"
                      >
                        hello@anvayee.com
                      </a>
                    </div>
                    <div>
                      <p className="text-small text-text-tertiary">Location</p>
                      <p className="text-base text-text-primary">Hyderabad, India</p>
                    </div>
                    <div>
                      <p className="text-small text-text-tertiary">Timezone</p>
                      <p className="text-base text-text-primary">India Standard Time (IST)</p>
                    </div>
                  </div>
                </div>

                <div className="pt-8 border-t border-border-subtle">
                  <h3 className="text-h4 font-medium text-text-primary">Prefer to talk?</h3>
                  <Body className="mt-3">
                    Book a 30-minute consultation. We'll understand your situation and
                    tell you honestly if we can help.
                  </Body>
                  <div className="mt-6">
                    <Link to="/book">
                      <Button variant="secondary">
                        Book a consultation
                      </Button>
                    </Link>
                  </div>
                </div>

                <div className="pt-8 border-t border-border-subtle">
                  <h3 className="text-h4 font-medium text-text-primary">Response time</h3>
                  <Body className="mt-3">
                    We typically respond within 24-48 hours during business days.
                    For urgent matters, please book a consultation directly.
                  </Body>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      <Spacer size="lg" />
    </main>
  );
}
